/**
 * LocalPersistenceUtils.js
 * 
 * Storage and persistence utilities for tokens and data
 * Supports: sessionStorage, localStorage, cookies, memory
 * Optional encryption using EncryptionUtils
 */

import getDomain from './DomainParser';
import { decryptValue, encryptValue, isEncrypted } from './EncryptionUtils';

/**
 * Retrieves and optionally decrypts a stored token
 * @param {string} storageType - Type of storage (sessionStorage, localStorage, cookie, memory)
 * @param {string} key - Storage key
 * @param {string|null} encryptionKey - Optional encryption key for decryption (null = no decryption)
 * @returns {Promise<string|null>} - The stored value (decrypted if encryption key provided)
 */
const getStoredToken = async (storageType, key, encryptionKey = null) => {
  let value;

  switch (storageType) {
    case 'sessionStorage':
      value = sessionStorage.getItem(key);
      break;
    case 'localStorage':
      value = localStorage.getItem(key);
      break;
    case 'cookie':
      value = getCookie(key);
      break;
    case 'memory':
    default:
      value = window[`__${key}__`];
      break;
  }

  // Decrypt if encryption key is provided and value exists
  if (value && encryptionKey) {
    const decrypted = await decryptValue(value, encryptionKey);
    // If decryption returns null (failed), return the original value (likely plaintext)
    return decrypted !== null ? decrypted : value;
  }

  return value;
};

/**
 * Stores and optionally encrypts a token
 * @param {string} storageType - Type of storage (sessionStorage, localStorage, cookie, memory)
 * @param {string} key - Storage key
 * @param {string} token - Token value to store
 * @param {string|null} domain - Cookie domain (only used for cookie storage)
 * @param {string|null} encryptionKey - Optional encryption key for encryption (null = no encryption)
 * @returns {Promise<void>}
 */
const setStoredToken = async (storageType, key, token, domain = null, encryptionKey = null) => {
  // Encrypt if encryption key is provided
  const finalToken = encryptionKey
    ? await encryptValue(token, encryptionKey)
    : token;

  switch (storageType) {
    case 'sessionStorage':
      sessionStorage.setItem(key, finalToken);
      break;
    case 'localStorage':
      localStorage.setItem(key, finalToken);
      break;
    case 'cookie':
      setCookie(key, finalToken, 14, domain); // 14 days (encryption already applied)
      break;
    case 'memory':
    default:
      window[`__${key}__`] = finalToken;
      break;
  }
};


const getStandardMicrospecCookie = (key) => {
  return getCookie(key);
};

const setStandardMicrospecCookie = (key, token) => {
  // Standard Microspec cookie settings: 14 days, domain auto-detected
  setCookie(key, token, 14, getDomain(window.location.hostname));
};

/**
 * Clears a stored token
 * @param {string} storageType - Type of storage
 * @param {string} key - Storage key
 */
const clearStoredToken = (storageType, key) => {
  switch (storageType) {
    case 'sessionStorage':
      sessionStorage.removeItem(key);
      break;
    case 'localStorage':
      localStorage.removeItem(key);
      break;
    case 'cookie':
      deleteCookie(key);
      break;
    case 'memory':
    default:
      delete window[`__${key}__`];
      break;
  }
};

/**
 * Sets a cookie with the specified parameters
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value (already encrypted if needed)
 * @param {number} days - Expiration in days
 * @param {string|null} domain - Cookie domain (optional)
 */
const setCookie = (name, value, days, domain = null) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  let cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;

  // Add domain if specified
  if (domain) {
    cookieString += `;domain=${domain}`;
  }

  document.cookie = cookieString;
};

/**
 * Gets a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }

  return null;
};

/**
 * Deletes a cookie
 * @param {string} name - Cookie name
 */
const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Migrates plaintext data to encrypted format
 * @param {string} storageType - Type of storage
 * @param {string} key - Storage key
 * @param {string} encryptionKey - Encryption key to use
 * @param {string|null} domain - Cookie domain (for cookie storage)
 * @returns {Promise<boolean>} - True if migration occurred, false if already encrypted
 */
const migrateToEncrypted = async (storageType, key, encryptionKey, domain = null) => {
  // Get value directly from storage (bypass getStoredToken to avoid decryption attempt)
  let currentValue;
  switch (storageType) {
    case 'sessionStorage':
      currentValue = sessionStorage.getItem(key);
      break;
    case 'localStorage':
      currentValue = localStorage.getItem(key);
      break;
    case 'cookie':
      currentValue = getCookie(key);
      break;
    case 'memory':
      currentValue = window[`__${key}__`];
      break;
    default:
      return false;
  }

  // Check if already encrypted
  if (!currentValue || isEncrypted(currentValue)) {
    return false; // Already encrypted or no value
  }

  // Re-store with encryption
  await setStoredToken(storageType, key, currentValue, domain, encryptionKey);
  return true; // Migration performed
};

// Export main functions
export {
  clearStoredToken, deleteCookie, getCookie, getStandardMicrospecCookie, getStoredToken, migrateToEncrypted, setCookie, setStandardMicrospecCookie, setStoredToken
};

// Export for browser testing (window.LocalPersistenceUtils)
if (typeof window !== 'undefined') {
  window.LocalPersistenceUtils = {
    getStoredToken,
    setStoredToken,
    getStandardMicrospecCookie,
    setStandardMicrospecCookie,
    clearStoredToken,
    setCookie,
    getCookie,
    deleteCookie,
    migrateToEncrypted,
    isEncrypted, // Re-export from EncryptionUtils for convenience
  };
}
